import {
  ActionType,
  getResourceLifecycleStatus,
  Listing,
  ListingColumn,
  ListingDisplayNameLink,
  SortDirections,
  stateT,
  Status,
} from "o4a-react";
import * as React from "react";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { AzureResourceGroupLink } from "../../components/AzureLinks/AzureResourceGroupLink";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import { DetailsPanelId, PageId, PageRegistrationConfig, RESOURCE_ROUTE } from "../../constants/pluginConstants";
import { ListingTestIds, MonochromeIconIds, ttlOneMinCaching } from "../../constants/uiConstants";
import {
  VmdbDatabaseSummary,
  VmdbDatabaseSummaryCollection,
  VmdbDbHomeSummary,
  VmdbDbHomeSummaryCollection,
  VmdbDbSystem,
} from "../../gen/clients/mchub-azure-api-client-vmdb";
import { getStatusInfo, optimizedRetryOption, responseItemstoArray } from "../../helpers/resourceHelper";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useQueryCall } from "../../hooks/useQueryCall";
import { useRowCount } from "../../hooks/useRowCount";
import { getAzureLocationName, getOciRegion } from "../../utils";

/**
 * TODO
 * This interface is implement to include the last status and details.
 * Api has not expose these two fields for now. When it will, we might remove this interface.
 */
interface VmdbDatabaseSummaryExt extends VmdbDatabaseSummary {
  lastOperationStatus?: string;
  lastOperationStatusDetails?: string;
}

export interface VmDbSystemDatabasesListProps {
  dbSystem: VmdbDbSystem | undefined;
  location: string;
  subscriptionId: string;
  resourceGroupName: string,
}

export enum ColumnIds {
  Name = "name",
  ResourceGroupName = "id",
  Location = "location",
  ResourceStatus = "status"
}

export enum ActionIds {
  Refresh = "refresh",
}

export enum ActionBarTestIds {
  Refresh = "refresh",
}

const detailsPageRegistrationIds = PageRegistrationConfig[PageId.VMDB_CDB_DETAILS].map(config => config.key);

export const VmDbSystemDatabasesList = (
  { dbSystem, location, subscriptionId, resourceGroupName }: VmDbSystemDatabasesListProps,
): JSX.Element => {
  const { trackActionClick } = useAnalytics();

  const { locale } = React.useContext<Settings>(SettingsContext);
  const { subDetailsListingItemCount } = useRowCount();

  const { response: responseDbHomes } = useQueryCall({
    wait: !(dbSystem?.id && location),
    method: apiClients.withRegion(getOciRegion(location)).vmDatabaseApi.listVmdbDbHomes,
    options: {
      args: {
        subscriptionId: subscriptionId || "",
        resourceGroupName,
        apiVersion: MultiCloudDatabaseApiVersion,
        dbSystemId: dbSystem?.id,
      },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadDatabaseHomes(),
      },
    },
  });

  const vmDbHomeItems = (responseDbHomes
    && responseItemstoArray<VmdbDbHomeSummaryCollection, VmdbDbHomeSummary>(responseDbHomes)) || [];

  const vmDbHomeId = vmDbHomeItems?.find(item => item.dbSystemId === dbSystem?.id)?.id;

  const { response: responseDbs, loading: loadingDbs, refresh: refreshDbs } = useQueryCall({
    wait: !vmDbHomeId,
    method: apiClients.withRegion(getOciRegion(location)).vmDatabaseApi.listVmdbDatabases,
    options: {
      args: {
        dbHomeId: vmDbHomeId,
        subscriptionId: subscriptionId || "",
        resourceGroupName,
        apiVersion: MultiCloudDatabaseApiVersion,
      },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadDatabases(),
      },
    },
  });

  const dbItems = responseDbs
    && responseItemstoArray<VmdbDatabaseSummaryCollection, VmdbDatabaseSummary>(responseDbs);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = React.useMemo(() => dbItems, [JSON.stringify(dbItems)]);

  const getLocation = (value: VmdbDatabaseSummaryExt): JSX.Element => (
    <div>{getAzureLocationName(value?.location || "")}</div>
  );

  const getStatus = (value: VmdbDatabaseSummaryExt): JSX.Element => (
    <Status
      label={stateT(value?.status || "")}
      tooltip={stateT(value?.status || "")}
      status={getResourceLifecycleStatus(value?.status || "")}
      statusInfo={
        getStatusInfo(
          value?.status || "",
          value?.lastOperationStatus || "",
          value?.lastOperationStatusDetails || "",
        )
      }
    />
  );

  const columns: ListingColumn[] = [
    {
      name: Messages.common.name(),
      itemProp: ColumnIds.Name,
      flexGrow: 1,
      isResizable: true,
      initialSortDirection: SortDirections.ASC,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => (
        <ListingDisplayNameLink
          displayName={value.name}
          navigation={{
            // eslint-disable-next-line max-len
            to: `${RESOURCE_ROUTE}/${value.id}/${PageRegistrationConfig[PageId.VMDB_CDB_DETAILS][0].panelPath}?location=${value.location}`,
            pageKey: detailsPageRegistrationIds[0],
          }}
        />
      ),
    },
    {
      name: Messages.labels.resourceGroup(),
      itemProp: ColumnIds.ResourceGroupName,
      flexGrow: 1,
      isResizable: true,
      initialSortDirection: SortDirections.ASC,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => <AzureResourceGroupLink resourceId={value.id} />,
    },
    {
      name: Messages.labels.location(),
      itemProp: ColumnIds.Location,
      flexGrow: 1,
      isResizable: true,
      initialSortDirection: SortDirections.ASC,
      onRenderItems: value => getLocation(value),
    },
    {
      name: Messages.labels.status(),
      itemProp: ColumnIds.ResourceStatus,
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      onRenderItems: value => getStatus(value),
    },
  ];

  const dbActions: ActionType[] = [
    {
      key: ActionIds.Refresh,
      testId: ActionBarTestIds.Refresh,
      text: Messages.actions.refresh(),
      icon: MonochromeIconIds.Refresh,
      onClick: () => {
        trackActionClick(ActionIds.Refresh, PageId.VMDB_SYSTEM_DETAILS, DetailsPanelId.CONTAINERDATABASES);
        refreshDbs();
      },
    },
  ];

  return (
    <Listing
      testId={ListingTestIds.VmDbSystemDatabases}
      isLoading={loadingDbs}
      itemKey={ColumnIds.Name}
      items={items || []}
      emptyList={{ title: Messages.common.noResults() }}
      listColumns={columns}
      itemsPerPage={subDetailsListingItemCount}
      actionBarItems={dbActions}
      sorting={{
        locale,
        initialSortedColumn: ColumnIds.Name,
      }}
    />
  );
};
